// Self-resolving chrome preview for surfaces that don't own a selector
// — grid tiles, mostly.
//
// It's a component rather than a bare call to <AdChromeFrame> because
// each tile resolves its own chrome from its own canvas shape, and a
// hook can't run inside a .map(). Detail views that DO own a selector
// should call useAdChrome() themselves and drive <AdChromeFrame>
// directly, so the selector and the frame share one piece of state.

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { AdChromeFrame } from './AdChromeFrame';
import { useAdChrome } from './useAdChrome';
import { useChromeIdentity } from './useChromeIdentity';
import { normalizeRatio } from './types';
import type { ChromeDensity, ChromeIdentity } from './types';

export type AdChromePreviewProps = {
  aspectRatio?:    string | null;
  width?:          number | null;
  height?:         number | null;
  platformFormat?: string | null;
  // Per-ad identity bits — the ad's own CTA copy, mostly.
  ctaText?:        string | null;
  identity?:       Partial<ChromeIdentity>;
  density?:        ChromeDensity;
  maxWidth?:       number;
  background?:     string;
  children:        ReactNode;
};

export function AdChromePreview({
  aspectRatio,
  width,
  height,
  platformFormat,
  ctaText,
  identity: identityOverrides,
  density = 'compact',
  maxWidth,
  background,
  children
}: AdChromePreviewProps) {
  const ratio = useMemo(
    () => normalizeRatio(aspectRatio, width, height),
    [aspectRatio, width, height]
  );
  const overrides = useMemo(
    () => ({ ctaText: ctaText ?? null, ...identityOverrides }),
    [ctaText, identityOverrides]
  );
  const identity = useChromeIdentity(overrides);
  const { chrome } = useAdChrome(ratio, platformFormat);

  return (
    <AdChromeFrame
      chrome={chrome}
      ratio={ratio}
      identity={identity}
      density={density}
      maxWidth={maxWidth}
      background={background}
    >
      {children}
    </AdChromeFrame>
  );
}
